import { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Mail, Heart, MapPin, Clock, ArrowDown } from 'lucide-react';
import { useIsMobile } from '../hooks/use-mobile';

const narrativeBeats = [
  {
    tag: "April 2003 • Blue John Canyon",
    title: "The Silent Departure",
    content: "Aron Ralston went hiking alone into the Utah desert. Like most of us, he didn't think twice. He didn't tell a single soul where he was going.",
    highlight: "No plans shared. No safety net.",
    icon: MapPin
  },
  {
    tag: "127 Hours of Isolation",
    title: "The Unforeseen Crisis",
    content: "When a falling boulder trapped him in a remote canyon, the physical pain was eclipsed by a much heavier realization.",
    highlight: "Nobody was looking for him, because nobody knew he was missing.",
    icon: Clock
  },
  {
    tag: "The Lifelong Vow",
    title: "A Promise Born From Survival",
    content: "Against all odds, he survived and made the ultimate sacrifice to return to the world. When he did, he made a solemn vow to himself and the people who loved him.",
    highlight: "Never again leave without someone knowing your destination.",
    icon: Mail
  }
];

export default function StorySection() {
  const containerRef = useRef<HTMLElement>(null);
  const isMobile = useIsMobile();
  
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start end", "end start"]
  });

  const ambientGlowY = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);

  return (
    <section 
      id="story" 
      ref={containerRef} 
      className="relative min-h-screen py-32 md:py-44 overflow-hidden bg-[#0a1628] text-white selection:bg-emerald-500 selection:text-[#0a1628]"
    >
      {/* Background Ambient Lighting from old.tsk */}
      <motion.div 
        className="absolute inset-0 opacity-40 pointer-events-none"
        style={{ y: ambientGlowY }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-[#0a1628] via-[#0f2440] to-[#0a1628]" />
        <div className="absolute top-1/4 left-1/4 w-[600px] h-[600px] bg-emerald-500/10 rounded-full blur-[120px]" />
        <div className="absolute bottom-1/4 right-1/4 w-[500px] h-[500px] bg-emerald-500/5 rounded-full blur-[100px]" />
      </motion.div>

      <div className="relative z-10 container mx-auto px-6 max-w-6xl">
        
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: isMobile ? 0 : 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="text-center mb-24 md:mb-32"
        >
          <span className="inline-block text-xs font-medium uppercase tracking-[0.3em] text-emerald-400/60 mb-6">
            A True Story
          </span>
          
          <h2 
            className="text-4xl md:text-6xl lg:text-7xl font-bold tracking-tight text-white mb-8 leading-[1.1]" 

          >
            The Inspiration
          </h2>
          
          <div className="w-24 h-px bg-emerald-500/30 mx-auto mb-8" />

          <p className="text-lg md:text-xl text-white/60 max-w-2xl mx-auto font-light leading-relaxed">
            Most safety tools are built for emergencies you expect. <br /> We built PingAfter for the quiet moments you can never predict.
          </p>

          <motion.div 
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: isMobile ? 0 : 0.5, duration: isMobile ? 0 : 1 }}
            className="mt-12 flex justify-center text-emerald-500/40"
          >
            <ArrowDown className="w-5 h-5 animate-bounce" />
          </motion.div>
        </motion.div>

        {/* Narrative Flow (Timeline) */}
        <div className="relative">
          {/* Vertical Guide Line */}
          <div className="absolute left-8 md:left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-transparent via-emerald-500/30 to-transparent md:-translate-x-1/2" />

          {narrativeBeats.map((beat, index) => {
            const Icon = beat.icon;
            const isEven = index % 2 === 0;
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: isEven ? -40 : 40, y: 20 }}
                whileInView={{ opacity: 1, x: 0, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: isMobile ? 0 : 0.8, delay: isMobile ? 0 : 0.2, ease: [0.16, 1, 0.3, 1] }}
                className={`relative flex items-start gap-8 mb-20 md:mb-32 ${
                  isEven ? 'md:flex-row' : 'md:flex-row-reverse'
                }`}
              >
                {/* Timeline Node */}
                <div className="absolute left-8 md:left-1/2 -translate-x-1/2 w-16 h-16 rounded-full bg-[#0a1628] border-2 border-emerald-500/30 flex items-center justify-center z-10 shadow-[0_0_20px_rgba(16,185,129,0.15)] mt-0 md:mt-2">
                  <Icon className="w-6 h-6 text-emerald-400" />
                </div>

                {/* Content */}
                <div className={`ml-24 md:ml-0 md:w-1/2 flex ${
                  isEven ? 'md:pr-20 md:justify-end' : 'md:pl-20 md:justify-start'
                }`}>
                  <div className="flex flex-col items-start text-left gap-4 max-w-lg">
                    <span className="inline-flex text-xs font-mono uppercase tracking-widest text-emerald-400/60 bg-emerald-500/5 px-3 py-1 rounded-full border border-emerald-500/10 w-fit">
                      {beat.tag}
                    </span>

                    <h3 className="text-2xl md:text-3xl font-semibold text-white tracking-tight">
                      {beat.title}
                    </h3>

                    <p className="text-base md:text-lg text-white/70 leading-relaxed font-light">
                      {beat.content.split('. ').map((sentence, i, arr) => (
                        <span key={i} className="block mb-2 last:mb-0">
                          {sentence}{i !== arr.length - 1 ? '.' : ''}
                        </span>
                      ))}
                    </p>

                    <div className="mt-2 p-4 rounded-xl bg-[#0f2440]/50 border border-emerald-500/10 w-fit">
                      <span className="text-sm md:text-base font-medium text-emerald-300/90 italic inline-block">
                        "{beat.highlight}"
                      </span>
                    </div>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* The Climax & Emotional Resolution / CTA */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-50px" }}
          transition={{ duration: isMobile ? 0 : 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="relative mt-20 md:mt-32 text-center"
        >
          <div className="absolute inset-0 bg-emerald-500/5 rounded-3xl blur-2xl -z-10" />
          
          <div className="relative z-10 max-w-4xl mx-auto p-10 md:p-16 rounded-3xl border border-white/10 bg-[#0f2440]/40 backdrop-blur-md shadow-xl overflow-hidden group">
            {/* Subtle highlight effect inside the card */}
            <div className="absolute inset-0 bg-gradient-to-br from-white/5 via-transparent to-transparent pointer-events-none" />
            
            <div className="relative z-20">
              {/* Animated Tower Icon */}
              <div className="mx-auto w-24 h-24 mb-10 relative flex items-center justify-center">
                <motion.div 
                  className="absolute inset-0 bg-emerald-500/10 rounded-full blur-xl"
                  animate={{ scale: [1, 1.1, 1], opacity: [0.3, 0.5, 0.3] }}
                  transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                />
                
                <svg viewBox="0 -20 200 320" className="w-16 h-16 text-emerald-400 relative z-10">
                  <g stroke="currentColor" strokeWidth="8" strokeLinecap="round" fill="none">
                    <motion.g
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: [0, 1, 0], scale: [0.8, 1.1, 1.2] }}
                      transition={{ duration: 2, repeat: Infinity, ease: "easeOut", delay: 0 }}
                      style={{ transformOrigin: "100px 69px" }}
                    >
                      <path d="M 80 34.4 A 40 40 0 0 0 80 103.6" />
                      <path d="M 120 34.4 A 40 40 0 0 1 120 103.6" />
                    </motion.g>

                    <motion.g
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: [0, 1, 0], scale: [0.8, 1.1, 1.2] }}
                      transition={{ duration: 2, repeat: Infinity, ease: "easeOut", delay: 0.4 }}
                      style={{ transformOrigin: "100px 69px" }}
                    >
                      <path d="M 67.5 12.7 A 65 65 0 0 0 67.5 125.3" />
                      <path d="M 132.5 12.7 A 65 65 0 0 1 132.5 125.3" />
                    </motion.g>

                    <motion.g
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: [0, 1, 0], scale: [0.8, 1.1, 1.2] }}
                      transition={{ duration: 2, repeat: Infinity, ease: "easeOut", delay: 0.8 }}
                      style={{ transformOrigin: "100px 69px" }}
                    >
                      <path d="M 55 -8.9 A 90 90 0 0 0 55 146.9" />
                      <path d="M 145 -8.9 A 90 90 0 0 1 145 146.9" />
                    </motion.g>
                  </g>

                  <g stroke="currentColor" strokeWidth="8" strokeLinecap="round" strokeLinejoin="round" fill="none">
                    <rect x="25" y="260" width="150" height="20" rx="8" />
                    <line x1="50" y1="260" x2="91" y2="82" />
                    <line x1="150" y1="260" x2="109" y2="82" />
                    <path d="M 50 260 L 139.5 215.5 L 71 171 L 118.5 126.5 L 91 82" />
                    <path d="M 150 260 L 60.5 215.5 L 129 171 L 81.5 126.5 L 109 82" />
                    <circle cx="100" cy="69" r="14" fill="#0a1628" />
                    <motion.circle 
                      cx="100" 
                      cy="69" 
                      r="6" 
                      fill="currentColor"
                      stroke="none"
                      animate={{ opacity: [0.3, 1, 0.3] }}
                      transition={{ duration: 1.5, repeat: Infinity }}
                    />
                  </g>
                </svg>
              </div>

              {/* MODIFIED: High-impact marketing headline directly answering the story's moral */}
              <h3 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white tracking-tight leading-tight mb-6">
                You have the right to your privacy. <br className="hidden md:block" />
                <span className="text-emerald-400 mt-2 block">
                  But if you need help, someone must know.
                </span>
              </h3>

              {/* MODIFIED: Concise, persuasive hook focused on peace of mind */}
              <p className="text-base md:text-lg text-white/70 font-light leading-relaxed max-w-xl mx-auto mb-10">
                You shouldn't have to sacrifice your independence or share your live location 24/7 to stay safe. <br /> Set a private timer, and we'll only alert your contacts if you don't check in.
              </p>

              {/* NEW: Actionable Call To Action (CTA) Button & Guarantee Note */}
              <div className="flex flex-col items-center justify-center gap-4">
                <a
                  href="#pricing"
                  className="w-full sm:w-auto inline-flex items-center justify-center px-8 py-4 rounded-full bg-emerald-500 hover:bg-emerald-400 text-[#0a1628] font-bold text-lg transition-all duration-300 shadow-lg hover:-translate-y-0.5"
                >
                  Start Your Free Safety Net
                </a>
                
                <span className="text-sm text-white/50 font-medium">
                  No credit card required to start
                </span>
              </div>

            </div>
          </div>
        </motion.div>

      </div>
    </section>
  );
}